/**
 * "Run failed items": once a run finishes with failures, run again only what
 * failed. Items the agent failed on (or never reached) call the agent again
 * and are scored; with none of those, the evaluator calls that errored, and
 * checks a stopped or failed run never ran, are scored on the stored outputs
 * (after an agent re-run, any left show the button again). Everything that worked keeps its answer and its scores.
 */

import { Button } from "@/components/ui/button";
import { errorText } from "@/components/ui/states";
import { useUiStore } from "@/stores/ui-store";
import { useExperimentAction, useRescore } from "../hooks";
import { overallTally } from "../scoring/scoring-tally";
import { isFinished } from "../status";
import type { ExperimentSummary } from "../types";
import { ProjectAction } from "@/features/projects/project-action";

/** What "Run failed items" would retry on a finished experiment: the agent calls, else the scores, else nothing. */
export function failedToRetry(e: ExperimentSummary): "agent" | "scores" | null {
  if (!isFinished(e.status)) return null;
  if (e.runner === "agent" && e.counts.agent_error + e.counts.not_run > 0) return "agent";
  // Checks a run gave up on never ran: a retry scores those as well as the ones that failed.
  const tally = overallTally(e);
  const scoreErrors =
    Object.values(e.aggregate?.evaluators ?? {}).some((a) => a.errors > 0) ||
    (tally?.failed ?? 0) + (tally?.pending ?? 0) > 0;
  return scoreErrors ? "scores" : null;
}

export function RunFailedButton({ experiment: e }: { experiment: ExperimentSummary }) {
  const action = useExperimentAction(e.id);
  const retry = useRescore();
  const notify = useUiStore((s) => s.notify);
  const failed = failedToRetry(e);
  if (!failed) return null;
  const agentFailed = failed === "agent";
  const handlers = {
    onSuccess: () => notify("Running the failed items again."),
    onError: (err: unknown) => notify(errorText(err, "That did not work. Try again."), "error"),
  };
  return (
    <ProjectAction permission="experiments:write">
      <Button
        disabled={action.isPending || retry.isPending}
        onClick={() =>
          agentFailed
            ? action.mutate("rerun-failed", handlers)
            : retry.mutate({ experimentId: e.id, body: { scope: "failed" } }, handlers)
        }
      >
        Run failed items
      </Button>
    </ProjectAction>
  );
}
